import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import "./QuotaTable.scss";
import Tooltip from "../../components/Tooltip";

// نصاب الحصص الموحّد لكل المدارس: عدد حصص كل مادة لكل صف.
// تسحبه الجداول المدرسية (نِصاب الحصص ← استيراد من الفارس).
const API = "https://api.alfares.cloud/api/alfares/quota";
const MAX_PERIODS = 20;

const getToken = () => JSON.parse(localStorage.getItem("userCredentials") || "null")?.token;
const errMsg = (err, fallback) => err?.response?.data?.message || fallback;
const cellKey = (g, s) => `${g}:${s}`;

export default function QuotaTable() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [cells, setCells] = useState({}); // "grade:subject" → نص الخانة
  const [dirty, setDirty] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [editing, setEditing] = useState(null); // { id, name }
  const [popupGrade, setPopupGrade] = useState(null); // بوب-أب التعديل السريع لصف معين

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/${getToken()}`);
      setGrades(data.grades || []);
      setSubjects(data.subjects || []);
      const next = {};
      for (const q of data.quotas || []) next[cellKey(q.grade_index, q.subject_id)] = String(q.periods);
      setCells(next);
      setDirty(false);
    } catch (err) {
      toast.error(errMsg(err, "تعذّر جلب النصاب"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // تنبيه قبل مغادرة الصفحة والتعديلات غير محفوظة
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const invalid = useMemo(() => {
    const bad = new Set();
    for (const [k, v] of Object.entries(cells)) {
      if (v === "") continue;
      const n = Number(v);
      if (!Number.isInteger(n) || n < 0 || n > MAX_PERIODS) bad.add(k);
    }
    return bad;
  }, [cells]);

  const rowTotal = (g) => subjects.reduce((sum, s) => {
    const n = Number(cells[cellKey(g, s.id)]);
    return Number.isInteger(n) && n > 0 ? sum + n : sum;
  }, 0);

  const setCell = (g, s, value) => {
    setCells((prev) => ({ ...prev, [cellKey(g, s)]: value.replace(/[^\d]/g, "").slice(0, 2) }));
    setDirty(true);
  };

  const save = async () => {
    if (invalid.size) {
      toast.error(`عدد الحصص في كل خانة يجب أن يكون من 0 إلى ${MAX_PERIODS}؛ صحّح الخانات الحمراء.`);
      return false;
    }
    const quotas = [];
    for (let g = 0; g < grades.length; g++) {
      for (const s of subjects) {
        const v = cells[cellKey(g, s.id)];
        if (v) quotas.push({ grade_index: g, subject_id: s.id, periods: Number(v) });
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/${getToken()}`, { quotas });
      setDirty(false);
      toast.success("تم حفظ النصاب");
      return true;
    } catch (err) {
      toast.error(errMsg(err, "تعذّر حفظ النصاب"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // عمليات المواد تعيد تحميل الجدول، فنحفظ التعديلات أولاً حتى لا تضيع
  const guardDirty = () => {
    if (!dirty) return true;
    toast.error("احفظ تعديلات الحصص أولاً، ثم عدّل المواد.");
    return false;
  };

  const addSubject = async (e) => {
    e.preventDefault();
    const name = newSubject.trim();
    if (!name || !guardDirty()) return;
    try {
      await axios.post(`${API}/subjects/${getToken()}`, { name });
      setNewSubject("");
      await load();
    } catch (err) {
      toast.error(errMsg(err, "تعذّر إضافة المادة"));
    }
  };

  const renameSubject = async () => {
    const name = editing?.name.trim();
    if (!name) return;
    try {
      await axios.put(`${API}/subjects/${editing.id}/${getToken()}`, { name });
      setSubjects((prev) => prev.map((s) => (s.id === editing.id ? { ...s, name } : s)));
      setEditing(null);
    } catch (err) {
      toast.error(errMsg(err, "تعذّر تعديل المادة"));
    }
  };

  const moveSubject = async (index, dir) => {
    const to = index + dir;
    if (to < 0 || to >= subjects.length) return;
    const next = [...subjects];
    [next[index], next[to]] = [next[to], next[index]];
    setSubjects(next);
    try {
      await axios.put(`${API}/subjects/order/${getToken()}`, { ids: next.map((s) => s.id) });
    } catch (err) {
      toast.error(errMsg(err, "تعذّر حفظ الترتيب"));
      load();
    }
  };

  const deleteSubject = (subject) => {
    if (!guardDirty()) return;
    toast(
      (t) => (
        <div className="confirm-toast">
          <p>حذف مادة «{subject.name}» وحصصها من كل الصفوف؟</p>
          <div className="toast-actions">
            <button
              className="btn-confirm"
              onClick={async () => {
                toast.dismiss(t.id);
                try {
                  await axios.delete(`${API}/subjects/${subject.id}/${getToken()}`);
                  toast.success("تم حذف المادة");
                  await load();
                } catch (err) {
                  toast.error(errMsg(err, "تعذّر حذف المادة"));
                }
              }}
            >
              نعم، احذف
            </button>
            <button className="btn-cancel" onClick={() => toast.dismiss(t.id)}>إلغاء</button>
          </div>
        </div>
      ),
      { duration: 6000 }
    );
  };

  return (
    <div className="quota-page anim-fade-in">
      <div className="premium-edit-container" style={{ maxWidth: "100%" }}>
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <i className="fa-solid fa-table-cells"></i>
            </div>
            <div className="header-text-box">
              <h2>نصاب الحصص</h2>
              <div className="breadcrumb-info">
                <span>الجداول المدرسية</span>
                <span className="separator">/</span>
                <span className="current-page">حصص كل مادة لكل صف (موحّد لكل المدارس)</span>
              </div>
            </div>
          </div>

          <div className="header-tools">
            <form className="quota-add" onSubmit={addSubject}>
              <input
                value={newSubject}
                onChange={(e) => setNewSubject(e.target.value)}
                placeholder="اسم مادة جديدة"
                maxLength={60}
              />
              <Tooltip content="إضافة مادة">
                <button type="submit" className="premium-action-btn btn--icon-mobile" aria-label="إضافة مادة" disabled={!newSubject.trim()}>
                  <i className="fa-solid fa-plus"></i>
                  <span className="btn-label">إضافة مادة</span>
                </button>
              </Tooltip>
            </form>
            <Tooltip content="حفظ">
              <button className="premium-action-btn btn--icon-mobile btn--gold" aria-label="حفظ" onClick={save} disabled={saving || !dirty}>
                <i className="fa-solid fa-floppy-disk"></i>
                <span className="btn-label">{saving ? "جارٍ الحفظ..." : "حفظ"}</span>
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="premium-table-container quota-scroll">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ تحميل البيانات...</span>
            </div>
          ) : subjects.length === 0 ? (
            <div className="quota-empty">لا يوجد مواد بعد. أضف أول مادة من الأعلى لتظهر أعمدة الجدول.</div>
          ) : (
            <table className="quota-table">
              <thead>
                <tr>
                  <th className="quota-grade">الصف</th>
                  {subjects.map((s, i) => (
                    <th key={s.id} className="quota-subject">
                      {editing?.id === s.id ? (
                        <input
                          autoFocus
                          className="quota-rename"
                          value={editing.name}
                          maxLength={60}
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") renameSubject();
                            if (e.key === "Escape") setEditing(null);
                          }}
                          onBlur={renameSubject}
                        />
                      ) : (
                        <Tooltip content="اضغط مرتين لتعديل الاسم">
                          <span className="quota-subject-name"
                            onDoubleClick={() => setEditing({ id: s.id, name: s.name })}>
                            {s.name}
                          </span>
                        </Tooltip>
                      )}
                      <span className="quota-subject-tools">
                        <Tooltip content="تحريك يميناً">
                          <button onClick={() => moveSubject(i, -1)} disabled={i === 0}>
                            <i className="fa-solid fa-chevron-right"></i>
                          </button>
                        </Tooltip>
                        <button onClick={() => setEditing({ id: s.id, name: s.name })}>
                          <i className="fa-solid fa-pen"></i>
                        </button>
                        <button className="danger" onClick={() => deleteSubject(s)}>
                          <i className="fa-solid fa-trash"></i>
                        </button>
                        <Tooltip content="تحريك يساراً">
                          <button onClick={() => moveSubject(i, 1)} disabled={i === subjects.length - 1}>
                            <i className="fa-solid fa-chevron-left"></i>
                          </button>
                        </Tooltip>
                      </span>
                    </th>
                  ))}
                  <th className="quota-total">المجموع</th>
                </tr>
              </thead>
              <tbody>
                {grades.map((grade, g) => (
                  <tr key={g}>
                    <td
                      className="quota-grade quota-grade-click"
                      onClick={() => setPopupGrade(g)}
                      title="اضغط للتعديل السريع"
                    >
                      {grade}
                    </td>
                    {subjects.map((s) => {
                      const k = cellKey(g, s.id);
                      return (
                        <td key={s.id} className="quota-cell">
                          <input
                            inputMode="numeric"
                            value={cells[k] ?? ""}
                            onChange={(e) => setCell(g, s.id, e.target.value)}
                            className={invalid.has(k) ? "invalid" : ""}
                            title={invalid.has(k) ? `من 0 إلى ${MAX_PERIODS} حصة` : ""}
                          />
                        </td>
                      );
                    })}
                    <td className="quota-total">{rowTotal(g) || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* بوب-أب التعديل السريع: مواد الصف المضغوط في أعمدة */}
        {popupGrade !== null && (
          <div className="quota-popup-overlay" onClick={() => setPopupGrade(null)}>
            <div className="quota-popup" onClick={(e) => e.stopPropagation()}>
              <div className="quota-popup-head">
                <h3>تعديل سريع — {grades[popupGrade]}</h3>
                <button
                  className="quota-popup-close"
                  onClick={() => setPopupGrade(null)}
                  aria-label="إغلاق"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
              <div className="quota-popup-grid">
                {subjects.map((s) => {
                  const k = cellKey(popupGrade, s.id);
                  return (
                    <div key={s.id} className="qp-item">
                      <span className="qp-name">{s.name}</span>
                      <input
                        inputMode="numeric"
                        value={cells[k] ?? ""}
                        onChange={(e) => setCell(popupGrade, s.id, e.target.value)}
                        className={invalid.has(k) ? "invalid" : ""}
                        aria-label={`${s.name} - ${grades[popupGrade]}`}
                      />
                    </div>
                  );
                })}
                <div className="qp-item qp-total-card">
                  <span className="qp-name">المجموع</span>
                  <span className="qp-total-num">{rowTotal(popupGrade) || 0}</span>
                </div>
              </div>
              <div className="quota-popup-foot">
                <p className="quota-popup-hint">
                  التعديلات هنا فورية على نفس البيانات.
                </p>
                <button
                  className="qp-save-btn"
                  disabled={saving}
                  onClick={async () => {
                    if (await save()) setPopupGrade(null);
                  }}
                >
                  <i className="fa-solid fa-floppy-disk"></i>
                  {saving ? "جارٍ الحفظ..." : "حفظ"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
